import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Moon, Loader2, Image, FileDown, Crown, X, Share2, Check, Trash2, MoreVertical, Eye } from "lucide-react";
import axios from "axios";
import StarCanvas from "@/components/StarCanvas";
import VoicePlayer from "@/components/VoicePlayer";
import { parseStory } from "@/components/StoryGenerator";
import { READER } from "@/constants/testIds";
import useActiveDraft from "@/hooks/useActiveDraft";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const themeLabel = (t) =>
  t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Bedtime Adventure";

function ReaderIllustration({ storyId, prompt, index, ready, canGenerate, onGenerated }) {
  const [isReady, setIsReady] = useState(ready);
  const [loading, setLoading] = useState(false);

  // Sync with the `ready` prop when it flips true later (e.g. a batch job
  // finishes this index) — useState's initializer only runs on first mount.
  useEffect(() => {
    if (ready && !isReady) setIsReady(true);
  }, [ready, isReady]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/stories/${storyId}/illustrations/${index}`, {}, { withCredentials: true });
      setIsReady(true);
      if (onGenerated) onGenerated(index);
    } catch (err) {
      console.error("Illustration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-8 rounded-2xl overflow-hidden border border-purple-500/20">
      {isReady ? (
        <div className="relative">
          <img
            src={`${API}/stories/${storyId}/illustrations/${index}`}
            alt={`Story illustration ${index + 1}`}
            className="w-full aspect-[4/3] max-h-[45vh] sm:max-h-[500px] object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-xs text-slate-300 font-serif italic">{prompt}</p>
          </div>
        </div>
      ) : (
        <div className="aspect-[4/3] max-h-[45vh] sm:max-h-[500px] flex flex-col items-center justify-center gap-4 p-6" style={{ background: "rgba(22,18,48,0.5)" }}>
          {loading ? (
            <>
              <div className="w-14 h-14 rounded-full border-2 border-purple-500/30 border-t-amber-400 animate-spin" />
              <p className="text-sm text-slate-400 font-serif italic">Painting watercolors...</p>
            </>
          ) : (
            <>
              <Image size={22} className="text-purple-400" />
              <p className="text-sm text-slate-400 font-serif italic text-center max-w-sm">{prompt}</p>
              {canGenerate && (
                <button
                  onClick={handleGenerate}
                  className="px-5 py-2 rounded-full bg-purple-600/30 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/50 transition-colors"
                >
                  Generate Illustration
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function StoryReader() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [user, setUser] = useState(null);
  const draft = useActiveDraft(user);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUpsell, setPdfUpsell] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [shareState, setShareState] = useState("idle");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [batchStatus, setBatchStatus] = useState(null);
  const batchPollRef = useRef(null);
  const genPollRef = useRef(null);

  useEffect(() => () => {
    if (batchPollRef.current) clearInterval(batchPollRef.current);
  }, []);

  const startBatchPolling = (sid) => {
    if (batchPollRef.current) return;
    batchPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/stories/${sid}/illustrations/status`, { withCredentials: true });
        setStory((s) => ({ ...s, illustrations_ready: res.data.ready }));
        setBatchStatus({ ready: res.data.ready.length, total: res.data.total });
        if (res.data.ready.length >= res.data.total) {
          clearInterval(batchPollRef.current);
          batchPollRef.current = null;
        }
      } catch (_) {}
    }, 2500);
  };

  const handleGenerateAllIllustrations = async () => {
    if (!story || batchPollRef.current) return;
    try {
      const { data } = await axios.post(
        `${API}/stories/${story.story_id}/illustrations/generate-all`, {},
        { withCredentials: true }
      );
      setBatchStatus({ ready: data.ready, total: data.total });
      if (data.status === "processing") startBatchPolling(story.story_id);
    } catch (err) {
      console.error("Batch illustration error:", err);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/stories/${storyId}`, { withCredentials: true });
      navigate("/my-stories");
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Could not delete story. Please try again.");
      setDeleting(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Turn off this story's public link? Anyone with the old link will no longer see the story.")) return;
    setShareState("revoking");
    try {
      await axios.delete(`${API}/stories/${storyId}/share`, { withCredentials: true });
      setStory((s) => ({ ...s, share_token: undefined }));
      setShareState("revoked");
      setTimeout(() => setShareState("idle"), 2500);
    } catch {
      setShareState("idle");
      setPdfError("Could not revoke the link. Please try again.");
    }
  };

  const handleShare = async () => {
    setShareState("loading");
    try {
      const { data } = await axios.post(`${API}/stories/${storyId}/share`, {}, { withCredentials: true });
      setStory((s) => ({ ...s, share_token: data.share_token, share_view_count: data.share_view_count }));
      const link = `${window.location.origin}/shared/${data.share_token}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        window.prompt("Copy this magic link:", link);
      }
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 2500);
    } catch {
      setShareState("idle");
      setPdfError("Could not create the share link. Please try again.");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/stories/${storyId}`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => null),
    ]).then(([st, me]) => {
      if (st) setStory(st.data);
      else setNotFound(true);
      setUser(me ? me.data : false);
      setLoading(false);
    });
  }, [storyId]);

  // Story text generation runs server-side in the background (survives tab close) —
  // poll until it's ready so a story opened mid-generation (e.g. from My Stories) finishes live.
  useEffect(() => {
    if (!story || story.generation_status !== "generating" || genPollRef.current) return;
    genPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/stories/${story.story_id}/generation-status`, { withCredentials: true });
        const g = res.data;
        setStory((s) => ({
          ...s,
          story_text: g.story_text,
          title: g.title || s.title,
          illustration_prompts: g.illustration_prompts,
          word_count: g.word_count,
          generation_status: g.status,
          generation_error: g.error,
        }));
        if (g.status !== "generating") {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
        }
      } catch (_) {}
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.story_id, story?.generation_status]);

  useEffect(() => () => { if (genPollRef.current) clearInterval(genPollRef.current); }, []);

  // Detect a batch that's already running server-side (started from the home
  // generator or another tab) by checking if the ready-count advances within
  // a short window — avoids showing a false "in progress" spinner forever.
  useEffect(() => {
    if (!story) return;
    const total = (story.illustration_prompts || []).length;
    const readyCount = (story.illustrations_ready || []).length;
    if (total === 0 || readyCount >= total || batchPollRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const first = await axios.get(`${API}/stories/${story.story_id}/illustrations/status`, { withCredentials: true });
        await new Promise((r) => setTimeout(r, 3000));
        if (cancelled) return;
        const second = await axios.get(`${API}/stories/${story.story_id}/illustrations/status`, { withCredentials: true });
        if (cancelled) return;
        setStory((s) => ({ ...s, illustrations_ready: second.data.ready }));
        if (second.data.ready.length > first.data.ready.length) {
          setBatchStatus({ ready: second.data.ready.length, total: second.data.total });
          startBatchPolling(story.story_id);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.story_id]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    setPdfUpsell(false);
    setPdfError("");
    try {
      const res = await axios.get(`${API}/stories/${storyId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(story?.title || "DreamWeaver Story").replace(/[^\w\s-]/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 403) setPdfUpsell(true);
      else setPdfError("Could not build your storybook PDF. Please try again in a moment.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A]">
        <Loader2 size={32} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (notFound || !story) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A] px-4">
        <StarCanvas />
        <div className="relative z-10 glass-card rounded-3xl p-12 max-w-md w-full text-center">
          <Moon size={36} className="text-purple-400 mx-auto mb-5" />
          <h2 className="font-serif text-2xl font-bold text-white mb-2">Story not found</h2>
          <p className="text-slate-400 text-sm mb-6">This adventure may have drifted off to dreamland.</p>
          <button onClick={() => navigate("/my-stories")} className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm">
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const parts = parseStory(story.story_text);
  const ready = new Set(story.illustrations_ready || []);
  const isPremium = user && user.is_subscribed;

  return (
    <div data-testid={READER.page} className="relative min-h-screen bg-[#0B091A] overflow-x-clip">
      <StarCanvas />
      <div className="relative z-10">
        {/* Top bar */}
        <header className="sticky top-0 z-40 glass-card border-b border-purple-500/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
            <button
              data-testid={READER.backBtn}
              onClick={() => navigate("/my-stories")}
              className="flex items-center gap-2 min-h-[44px] min-w-[44px] px-1 text-sm text-slate-300 hover:text-amber-300 transition-colors shrink-0"
            >
              <ArrowLeft size={15} /> <span className="hidden sm:inline">Library</span>
            </button>
            {draft && draft.storyId !== story.story_id && (
              <button
                data-testid="reader-draft-badge"
                onClick={() => navigate(`/story/${draft.storyId}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/30 transition-colors shrink-0"
              >
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Story in progress</span>
              </button>
            )}
            <div className="flex items-center gap-2">
              {/* Primary + secondary actions: inline on tablet/desktop */}
              <div className="hidden sm:flex items-center gap-2">
                <button
                  data-testid="share-story-btn"
                  onClick={handleShare}
                  disabled={shareState === "loading" || story.generation_status === "generating"}
                  className="flex items-center gap-2 min-h-[44px] px-3 sm:px-4 rounded-full text-xs font-semibold border border-purple-500/30 text-slate-300 hover:border-amber-400/40 hover:text-amber-300 transition-all disabled:opacity-50"
                >
                  {shareState === "copied" ? <Check size={13} className="text-emerald-400" /> : shareState === "loading" ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                  <span className="hidden sm:inline">{shareState === "copied" ? "Link copied!" : "Share"}</span>
                </button>
                <button
                  data-testid={READER.pdfBtn}
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading || story.generation_status === "generating"}
                  className={`flex items-center gap-2 min-h-[44px] px-3 sm:px-4 rounded-full text-xs font-semibold transition-all ${
                    isPremium
                      ? "bg-gradient-to-r from-purple-600 to-amber-500 text-white hover:shadow-lg hover:shadow-purple-500/30"
                      : "border border-amber-400/30 text-amber-300 hover:bg-amber-500/10"
                  } disabled:opacity-50`}
                >
                  {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : isPremium ? <FileDown size={13} /> : <Crown size={13} />}
                  <span className="hidden sm:inline">{pdfLoading ? "Building storybook..." : "Download PDF Storybook"}</span>
                </button>
                {story.share_token && (
                  <span data-testid="share-view-count" className="text-xs text-slate-500 whitespace-nowrap">
                    Opened {story.share_view_count || 0} {story.share_view_count === 1 ? "time" : "times"}
                  </span>
                )}
                {story.share_token && (
                  <button
                    data-testid="revoke-share-btn"
                    onClick={handleRevoke}
                    disabled={shareState === "revoking"}
                    title="Turn off the public link"
                    className="flex items-center gap-1.5 min-h-[44px] px-3 rounded-full text-xs font-semibold border border-red-400/25 text-red-300/90 hover:bg-red-500/10 hover:border-red-400/50 transition-all disabled:opacity-50"
                  >
                    {shareState === "revoking" ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                    Revoke
                  </button>
                )}
                <button
                  data-testid="reader-delete-btn"
                  onClick={() => setConfirmDelete(true)}
                  title="Delete this story"
                  className="flex items-center gap-1.5 min-h-[44px] min-w-[44px] justify-center px-3 rounded-full text-xs font-semibold border border-red-400/25 text-red-300/90 hover:bg-red-500/10 hover:border-red-400/50 transition-all"
                >
                  <Trash2 size={13} />
                </button>
              </div>

              {/* Everything collapsed into a single menu on mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="reader-more-actions-menu"
                    className="sm:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-purple-500/30 text-slate-300 hover:text-amber-300 hover:border-amber-400/40 transition-colors"
                  >
                    <MoreVertical size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#161230] border-purple-500/30 text-slate-200">
                  <DropdownMenuItem
                    data-testid="reader-more-share"
                    onClick={handleShare}
                    disabled={shareState === "loading" || story.generation_status === "generating"}
                    className="gap-2"
                  >
                    <Share2 size={13} /> {shareState === "copied" ? "Link copied!" : "Share"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="reader-more-pdf"
                    onClick={handleDownloadPdf}
                    disabled={pdfLoading || story.generation_status === "generating"}
                    className="gap-2"
                  >
                    {isPremium ? <FileDown size={13} /> : <Crown size={13} />} {isPremium ? "Download PDF" : "PDF (Premium)"}
                  </DropdownMenuItem>
                  {story.share_token && (
                    <DropdownMenuItem disabled className="gap-2 opacity-100 text-slate-400">
                      <Eye size={13} /> Opened {story.share_view_count || 0} {story.share_view_count === 1 ? "time" : "times"}
                    </DropdownMenuItem>
                  )}
                  {story.share_token && (
                    <DropdownMenuItem data-testid="reader-more-revoke" onClick={handleRevoke} className="gap-2 text-red-300 focus:text-red-200 focus:bg-red-500/10">
                      <X size={13} /> Revoke share link
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="reader-more-delete" onClick={() => setConfirmDelete(true)} className="gap-2 text-red-300 focus:text-red-200 focus:bg-red-500/10">
                    <Trash2 size={13} /> Delete story
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {shareState === "revoked" && (
            <p data-testid="revoke-success-msg" className="text-center text-xs text-emerald-400 pb-2">Link revoked</p>
          )}
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-8 pb-24">
          {/* PDF upsell */}
          {pdfUpsell && (
            <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-400/20 flex items-start gap-3">
              <Crown size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-300 font-medium">PDF Storybook Export is a Premium perk</p>
                <p className="text-xs text-slate-400 mt-0.5">Turn every story into a printable picture book your family can keep forever.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/#pricing" className="px-4 py-1.5 rounded-full bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors whitespace-nowrap">
                  Upgrade — $7/mo
                </Link>
                <button onClick={() => setPdfUpsell(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
              </div>
            </div>
          )}

          {pdfError && (
            <div data-testid="pdf-error-message" className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-400/20 text-sm text-red-300">
              {pdfError}
            </div>
          )}

          {/* Title */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mt-14 mb-10 text-center">
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80">DreamWeaver Story</span>
            <h1 data-testid={READER.title} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold gradient-text mt-4 leading-tight">
              {story.title || "Weaving your story..."}
            </h1>
            <p className="text-sm text-slate-500 mt-4">
              For {story.child_name}{story.sibling_name ? ` & ${story.sibling_name}` : `, age ${story.child_age}`} · {themeLabel(story.theme)}
              {story.word_count ? ` · ~${Math.ceil(story.word_count / 130)} min read` : ""}
            </p>
            {story.created_at && (
              <p className="text-xs text-slate-600 mt-1">
                Created {new Date(story.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </p>
            )}
            {story.generation_status === "generating" && (
              <div data-testid="reader-generating-banner" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/10 border border-purple-500/20 text-purple-300 text-xs font-medium">
                <Loader2 size={13} className="animate-spin" />
                Still weaving this story — it keeps going even if you leave this page.
              </div>
            )}
            {story.generation_status === "failed" && (
              <div data-testid="reader-generation-failed-banner" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-400/20 text-red-300 text-xs font-medium">
                Story generation failed{story.generation_error ? `: ${story.generation_error}` : ""}. Please try creating a new one.
              </div>
            )}
          </motion.div>

          {/* Story body */}
          <div className="glass-card rounded-2xl p-6 sm:p-10">
            {parts.some((p) => p.type === "illustration") && user && story.generation_status !== "generating" && (
              <div className="flex justify-center mb-8">
                {batchStatus && batchStatus.ready < batchStatus.total ? (
                  <div
                    data-testid="reader-batch-progress"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/10 border border-purple-500/20 text-purple-300 text-xs font-medium"
                  >
                    <Loader2 size={13} className="animate-spin" />
                    Painting illustrations... {batchStatus.ready}/{batchStatus.total}
                  </div>
                ) : (
                  (() => {
                    const total = parts.filter((p) => p.type === "illustration").length;
                    if (ready.size >= total) return null;
                    return (
                      <button
                        data-testid="reader-generate-all-btn"
                        onClick={handleGenerateAllIllustrations}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/40 transition-colors"
                      >
                        <Image size={13} />
                        Generate All Illustrations ({ready.size}/{total})
                      </button>
                    );
                  })()
                )}
              </div>
            )}
            {parts.map((part, i) =>
              part.type === "text" ? (
                <div key={i} className="story-body">
                  {part.content.split(/\n\n+/).filter(Boolean).map((p, j) => (
                    <p key={j}>{p.trim()}</p>
                  ))}
                </div>
              ) : (
                <ReaderIllustration
                  key={i}
                  storyId={story.story_id}
                  prompt={part.prompt}
                  index={part.index}
                  ready={ready.has(part.index)}
                  canGenerate={!!user}
                />
              )
            )}
          </div>
        </main>

        {/* Narration replay */}
        <div className="max-w-7xl mx-auto">
          <VoicePlayer storyId={story.story_id} storyTitle={story.title} user={user} onAuthRequired={() => navigate("/")} />
        </div>
      </div>

      {/* Delete confirm dialog */}
      {confirmDelete && (
        <div data-testid="reader-delete-modal" className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deleting && setConfirmDelete(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative glass-card rounded-3xl p-8 max-w-md w-full text-center border border-red-400/20"
          >
            <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-400/30 flex items-center justify-center mx-auto mb-5">
              <Trash2 size={20} className="text-red-300" />
            </div>
            <h3 className="font-serif text-xl font-bold text-white mb-2">Move to trash?</h3>
            <p className="text-xs text-slate-500 mb-6">
              &ldquo;{story.title}&rdquo; will move to your Trash for 7 days — you can restore it any time before it&apos;s gone forever.
            </p>
            {deleteError && <p className="text-xs text-red-400 mb-3">{deleteError}</p>}
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="reader-delete-cancel"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-5 py-2.5 rounded-full border border-purple-500/30 text-slate-300 text-xs font-semibold hover:text-white transition-colors disabled:opacity-50"
              >
                Keep it
              </button>
              <button
                data-testid="reader-delete-confirm"
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                {deleting && <Loader2 size={12} className="animate-spin" />}
                {deleting ? "Moving..." : "Yes, move to trash"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
